import { PageHeader } from "@/components/PageHeader";
import { useConfirm } from "@/contexts/ConfirmDialogContext";
import { api } from "@/lib/api";
import { formatDate } from "@/lib/utils";
import {
  Badge,
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
  Field,
  Input,
  Label,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Text,
  Textarea,
} from "@a7y/tailwind-catalyst";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type ChangeEvent } from "react";
import { CommissionStateBadge, formatCents, LoadingPanel } from "./shared";
import type { CommissionRow, CommissionState } from "./types";

type View = "commissions" | "clawbacks";
type StatusMessage = { type: "success" | "error"; text: string };

const STATE_OPTIONS: { value: string; label: string }[] = [
  { value: "all", label: "All states" },
  { value: "pending", label: "Pending" },
  { value: "due", label: "Due" },
  { value: "paid", label: "Paid" },
  { value: "voided", label: "Voided" },
  { value: "clawback", label: "Clawback" },
];

export function CommissionsPage() {
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const [view, setView] = useState<View>("commissions");
  const [state, setState] = useState<string>("all");
  const [affiliateId, setAffiliateId] = useState("");
  const [statusMessage, setStatusMessage] = useState<StatusMessage | null>(null);
  const [adjustTarget, setAdjustTarget] = useState<CommissionRow | null>(null);

  const showStatus = (type: "success" | "error", text: string) => {
    setStatusMessage({ type, text });
    setTimeout(() => setStatusMessage(null), 4000);
  };

  const listQuery = useQuery({
    queryKey: ["affiliate-commissions", { state, affiliateId }],
    queryFn: () =>
      api.affiliates.listCommissions({
        state,
        affiliateId: affiliateId.trim() || undefined,
      }),
    enabled: view === "commissions",
  });

  const clawbackQuery = useQuery({
    queryKey: ["affiliate-clawbacks"],
    queryFn: () => api.affiliates.listClawbacks(),
    enabled: view === "clawbacks",
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["affiliate-commissions"] });
    queryClient.invalidateQueries({ queryKey: ["affiliate-clawbacks"] });
  };

  const voidMutation = useMutation({
    mutationFn: (args: { id: string; reason: string }) =>
      api.affiliates.voidCommission(args.id, args.reason),
    onSuccess: () => {
      invalidate();
      showStatus("success", "Commission voided");
    },
    onError: (err: Error) => showStatus("error", `Void failed: ${err.message}`),
  });

  const handleVoid = async (row: CommissionRow) => {
    const ok = await confirm({
      title: `Void ${formatCents(row.amountCents, row.currency)} commission?`,
      description:
        "Voiding removes this commission from the affiliate's balance. Use for refunds/chargebacks before payout. A Slack alert is posted to ops.",
      confirmLabel: "Void commission",
      confirmColor: "red",
    });
    if (ok) voidMutation.mutate({ id: row.id, reason: "admin_void" });
  };

  const rows: CommissionRow[] =
    (view === "commissions" ? listQuery.data?.commissions : clawbackQuery.data?.commissions) ?? [];
  const isLoading = view === "commissions" ? listQuery.isLoading : clawbackQuery.isLoading;

  return (
    <div className="px-4 py-0">
      <PageHeader title="Commissions" subtitle="Earned commissions and clawback reconciliation" />

      {/* View sub-tabs */}
      <div className="mb-6 flex gap-0 border-b border-zinc-200 dark:border-zinc-700">
        {(["commissions", "clawbacks"] as View[]).map((v) => (
          <button
            key={v}
            onClick={() => setView(v)}
            className={`px-4 py-2 text-sm font-medium capitalize transition-colors ${
              view === v
                ? "border-b-2 border-blue-500 text-blue-600 dark:text-blue-400"
                : "text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300"
            }`}
          >
            {v}
          </button>
        ))}
      </div>

      {view === "commissions" && (
        <div className="mb-6 flex flex-wrap items-end gap-3">
          <Field className="w-48">
            <Label>State</Label>
            <Select value={state} onChange={(e: ChangeEvent<HTMLSelectElement>) => setState(e.target.value)}>
              {STATE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </Select>
          </Field>
          <Field className="w-72">
            <Label>Affiliate ID</Label>
            <Input
              value={affiliateId}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setAffiliateId(e.target.value)}
              placeholder="Filter by affiliate id"
            />
          </Field>
        </div>
      )}

      {statusMessage && (
        <div
          className={`mb-4 rounded-lg px-4 py-2.5 text-sm font-medium ${
            statusMessage.type === "success"
              ? "bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-400"
              : "bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-400"
          }`}
        >
          {statusMessage.text}
        </div>
      )}

      {isLoading ? (
        <LoadingPanel />
      ) : (
        <Table dense striped>
          <TableHead>
            <TableRow>
              <TableHeader>Affiliate</TableHeader>
              <TableHeader>State</TableHeader>
              <TableHeader>Amount</TableHeader>
              <TableHeader>Invoice</TableHeader>
              <TableHeader>Created</TableHeader>
              {view === "commissions" && <TableHeader className="text-right">Actions</TableHeader>}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={view === "commissions" ? 6 : 5} className="py-8 text-center">
                  <Text className="text-sm text-zinc-500">
                    {view === "clawbacks" ? "No clawbacks flagged" : "No commissions match"}
                  </Text>
                </TableCell>
              </TableRow>
            ) : (
              rows.map((c) => (
                <TableRow key={c.id}>
                  <TableCell className="text-sm">
                    {c.affiliateEmail || <span className="font-mono text-xs">{c.affiliateId}</span>}
                  </TableCell>
                  <TableCell>
                    <CommissionStateBadge state={c.state} />
                    {c.clawbackFlaggedForAdmin && view === "commissions" && (
                      <Badge color="red" className="ml-1">
                        Clawback
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="font-medium">
                    {formatCents(c.amountCents, c.currency)}
                  </TableCell>
                  <TableCell className="font-mono text-xs">{c.stripeInvoiceId}</TableCell>
                  <TableCell className="text-sm text-zinc-500">{formatDate(c.createdAt)}</TableCell>
                  {view === "commissions" && (
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button plain onClick={() => setAdjustTarget(c)}>
                          Adjust
                        </Button>
                        {canVoid(c.state) && (
                          <Button
                            outline
                            className="text-red-600 dark:text-red-400"
                            disabled={voidMutation.isPending}
                            onClick={() => handleVoid(c)}
                          >
                            Void
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      )}

      {adjustTarget && (
        <AdjustDialog
          commission={adjustTarget}
          onClose={() => setAdjustTarget(null)}
          onSaved={() => {
            invalidate();
            setAdjustTarget(null);
            showStatus("success", "Commission adjusted");
          }}
          onError={(msg) => showStatus("error", msg)}
        />
      )}
    </div>
  );
}

function canVoid(state: CommissionState): boolean {
  return state === "pending" || state === "due";
}

function AdjustDialog({
  commission,
  onClose,
  onSaved,
  onError,
}: {
  commission: CommissionRow;
  onClose: () => void;
  onSaved: () => void;
  onError: (msg: string) => void;
}) {
  const [amount, setAmount] = useState(String(commission.amountCents));
  const [reason, setReason] = useState("");

  const nextCents = amount.trim() === "" ? NaN : Number(amount);
  const invalid = Number.isNaN(nextCents) || nextCents < 0 || reason.trim().length < 3;

  const mutation = useMutation({
    mutationFn: () =>
      api.affiliates.adjustCommission(commission.id, {
        amountCents: nextCents,
        reason: reason.trim(),
      }),
    onSuccess: onSaved,
    onError: (err: Error) => onError(`Adjust failed: ${err.message}`),
  });

  return (
    <Dialog open onClose={onClose} size="lg">
      <DialogTitle>Adjust commission</DialogTitle>
      <DialogBody>
        <div className="mb-4 grid grid-cols-2 gap-3">
          <div className="rounded-lg bg-zinc-50 px-4 py-3 dark:bg-zinc-800/50">
            <Text className="text-xs uppercase tracking-wide text-zinc-500">Before</Text>
            <div className="mt-1 text-lg font-semibold text-zinc-900 dark:text-zinc-100">
              {formatCents(commission.amountCents, commission.currency)}
            </div>
          </div>
          <div className="rounded-lg bg-blue-50 px-4 py-3 dark:bg-blue-900/20">
            <Text className="text-xs uppercase tracking-wide text-blue-600 dark:text-blue-400">
              After
            </Text>
            <div className="mt-1 text-lg font-semibold text-blue-700 dark:text-blue-300">
              {Number.isNaN(nextCents)
                ? "—"
                : formatCents(nextCents, commission.currency)}
            </div>
          </div>
        </div>

        <Field>
          <Label>New amount (cents)</Label>
          <Input
            type="number"
            min={0}
            value={amount}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setAmount(e.target.value)}
          />
        </Field>
        <Field className="mt-4">
          <Label>Reason</Label>
          <Textarea
            value={reason}
            onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setReason(e.target.value)}
            rows={3}
            placeholder="Why is this commission being adjusted? (audited + posted to ops)"
          />
        </Field>
      </DialogBody>
      <DialogActions>
        <Button plain onClick={onClose} disabled={mutation.isPending}>
          Cancel
        </Button>
        <Button color="blue" disabled={invalid || mutation.isPending} onClick={() => mutation.mutate()}>
          {mutation.isPending ? "Saving..." : "Save adjustment"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
